import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Container, Typography, Button, Box, Alert, CircularProgress, Paper } from '@mui/material';
import { authService } from '../services/authService';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';

/**
 * Page reached when user clicks the verification link in email.
 * Calls backend to verify the token and shows success or error.
 */
const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!token?.trim()) {
      setStatus('error');
      setMessage('Invalid verification link. Please use the link from your email or request a new one.');
      return;
    }
    let cancelled = false;
    authService
      .verifyEmail(token)
      .then(() => {
        if (!cancelled) {
          setStatus('success');
          setMessage('Your email has been verified. You can now sign in.');
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setStatus('error');
          setMessage(e?.message || 'Verification failed. The link may have expired. Please request a new one.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        {status === 'loading' && (
          <>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Verifying your email...</Typography>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              Email verified
            </Typography>
            <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Button component={Link} to="/login" variant="contained" size="large">
              Sign in
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              Verification failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button component={Link} to="/login" variant="contained">
                Go to sign in
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Use the <Link to="/please-verify-email">Resend verification email</Link> page to get a new link.
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default VerifyEmailPage;
